/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";

import {
  colors,
  constantSemanticColors,
  radius,
  spacing,
} from "../../../../../global/emotion";
import type { CSSColorStrict } from "../../../../utils/colors/types/CSSColorStrict.types";
import type { AvatarProps } from "../Avatar/Avatar.emotion";
import { Avatar } from "../Avatar/Avatar.emotion";
import { Skeleton } from "../Skeleton/Skeleton.emotion";
import { Text } from "../Text/Text.emotion";

const avatarFullStyle: Interpolation = {
  height: "100%",
  width: "100%",
};
const avatarRelativeStyle: Interpolation = {
  position: "relative",
};
const avatarResponsiveStyle: Interpolation = {
  borderRadius: radius.full,
  objectFit: "cover",
  objectPosition: "center",
  width: "100%",
};
const baseStyle: Interpolation = {
  display: "flex",
  justifyContent: "center",
};
const baseColorStyle: Interpolation<{ color: CSSColorStrict }> = (props) => ({
  backgroundColor: props.color,
});
const bottomRightAvatarStyle: Interpolation = {
  bottom: 0,
  right: 0,
};
const sideAvatarStyle: Interpolation<{ color: CSSColorStrict }> = (props) => ({
  outline: `2px solid ${props.color}`,
  position: "absolute",
});
const topLeftAvatarStyle: Interpolation = {
  left: 0,
  top: 0,
};
const topLeftAvatarResponsiveStyle: Interpolation<{
  color: CSSColorStrict;
  imgSrc: string;
}> = (props) => ({
  backgroundImage: `url(${props.imgSrc})`,
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  borderColor: props.color,
  borderRadius: "50%",
  borderStyle: "solid",
  borderWidth: 2.5,
  height: "35%",
  left: 0,
  position: "absolute",
  top: 0,
  width: "35%",
});
const bottomRightAvatarResponsiveStyle: Interpolation<{
  color: CSSColorStrict;
  imgSrc: string;
}> = (props) => ({
  backgroundImage: `url(${props.imgSrc})`,
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  borderColor: props.color,
  borderRadius: "50%",
  borderStyle: "solid",
  borderWidth: 2.5,
  bottom: 0,
  height: "35%",
  position: "absolute",
  right: 0,
  width: "35%",
});
const infoContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  gap: spacing.s,
  overflowX: "hidden",
  textAlign: "center",
  width: "100%",
};
const infoContainerResponsiveStyle: Interpolation = {
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
};
const labelTextStyle: Interpolation = {
  color: colors.white,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
};
const subLabelTextStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
};
const responsiveContainerStyle: Interpolation = {
  alignItems: "center",
  borderRadius: radius.m,
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  height: "100%",
  justifyContent: "center",
  paddingBottom: 40,
  paddingTop: 40,
  position: "absolute",
  width: "100%",
};
const responsiveAvatarContainerStyle: Interpolation = {
  position: "relative",
  width: "50%",
};

const stylesSize: Record<string, Interpolation> = {
  xs: {
    borderRadius: 6,
    height: spacing.l,
    padding: spacing.t,
    width: spacing.l,
  },
  s: {
    borderRadius: radius.s,
    boxSizing: "content-box",
    height: spacing.xl,
    padding: spacing.xxs,
    width: spacing.xl,
  },
  m: {
    borderRadius: radius.ms,
    height: spacing.xxxl,
    padding: spacing.xs,
    width: spacing.xxxl,
  },
  xl: {
    borderRadius: "20px",
    height: "116px",
    padding: spacing.ms,
    width: "116px",
  },
  xxl: {
    alignItems: "center",
    borderRadius: radius.m,
    flexDirection: "column",
    gap: spacing.m,
    height: "200px",
    padding: `${spacing.l} ${spacing.m}`,
    width: "160px",
  },
  responsive: {
    borderRadius: radius.m,
    paddingBottom: "125%",
    position: "relative",
    width: "100%",
  },
};

export type AvatarCommunityProps = Omit<AvatarProps, "size" | "imgSrc"> & {
  color?: CSSColorStrict;
  description?: string;
  imgSrc?: string;
  size?: "xs" | "s" | "m" | "xl" | "xxl" | "responsive";
  topLeftImgSrc?: string;
  bottomRightImgSrc?: string;
  sublabel?: string;
  className?: string;
};

export const AvatarCommunity = ({
  color,
  size = "m",
  topLeftImgSrc,
  bottomRightImgSrc,
  className = "",
  ...props
}: AvatarCommunityProps) => {
  const communityColor = color ?? (colors.black as CSSColorStrict);

  const showCommunityInfo = size === "xxl" || size === "responsive";

  const AvatarContent = (
    <>
      {size === "responsive" ? (
        <div css={responsiveAvatarContainerStyle}>
          <img
            alt={props.name}
            src={props.imgSrc}
            css={avatarResponsiveStyle}
          />
          {topLeftImgSrc && (
            <div
              css={topLeftAvatarResponsiveStyle({
                color: communityColor,
                imgSrc: topLeftImgSrc,
              })}
            />
          )}
          {bottomRightImgSrc && (
            <div
              css={bottomRightAvatarResponsiveStyle({
                color: communityColor,
                imgSrc: bottomRightImgSrc,
              })}
            />
          )}
        </div>
      ) : (
        <Avatar
          size={size === "xxl" ? 96 : "full"}
          css={[size === "xxl" ? avatarRelativeStyle : avatarFullStyle]}
          {...props}
        >
          {size === "xxl" && (
            <>
              {topLeftImgSrc && (
                <Avatar
                  size={32}
                  imgSrc={topLeftImgSrc}
                  css={[
                    sideAvatarStyle({ color: communityColor }),
                    topLeftAvatarStyle,
                  ]}
                />
              )}
              {bottomRightImgSrc && (
                <Avatar
                  size={32}
                  imgSrc={bottomRightImgSrc}
                  css={[
                    sideAvatarStyle({ color: communityColor }),
                    bottomRightAvatarStyle,
                  ]}
                />
              )}
            </>
          )}
        </Avatar>
      )}
      {showCommunityInfo && (
        <div
          css={[
            infoContainerStyle,
            size === "responsive" && infoContainerResponsiveStyle,
          ]}
        >
          {props.name && (
            <Text type="title" size="m" css={labelTextStyle}>
              {props.name}
            </Text>
          )}
          {props.description && (
            <Text type="detail" size="l" css={subLabelTextStyle}>
              {props.description}
            </Text>
          )}
          {props.sublabel && (
            <Text type="detail" size="l" css={subLabelTextStyle}>
              {props.sublabel}
            </Text>
          )}
        </div>
      )}
    </>
  );

  if (size === "responsive" && props.loading) {
    return (
      <Skeleton
        css={[baseStyle, stylesSize.responsive]}
        className={className}
      />
    );
  }

  return (
    <div
      css={[baseColorStyle({ color: communityColor }), stylesSize[size]]}
      className={className}
    >
      {size === "responsive" ? (
        <div css={responsiveContainerStyle}>{AvatarContent}</div>
      ) : (
        AvatarContent
      )}
    </div>
  );
};
